import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import Button from "components/Button";
import Logo from "components/common/Logo";
import FormPasswordInput from "components/form/FormPasswordInput";
import FormTextInput from "components/form/FormTextInput";
import AuthSplitLayout from "layouts/AuthSplitLayout";
import { api } from "lib/api";
import { GENERAL_INQUIRIES_EMAIL } from "config/envConfig";
import swtAlert from "lib/sweetAlert";
import { validateEmail, required, validateForm as runFormValidation } from "lib/validation";
import {
  extractAuthPayload,
  isAuthenticated,
  isSuccessResponse,
} from "routes/auth";
import { DEFAULT_APP_PATH } from "routes/paths";
import { apiURl } from "store/actions";
import { SetAuthUserData } from "store/reducer";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: GENERAL_INQUIRIES_EMAIL,
    password: "",
  });
  const [errors, setErrors] = useState({});

  if (isAuthenticated()) {
    return <Navigate to={DEFAULT_APP_PATH} replace />;
  }

  const validateForm = () => {
    const newErrors = runFormValidation(formData, {
      email: (v) => validateEmail(v),
      password: required("Password"),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      email: formData.email.trim(),
      password: formData.password,
    };

    setIsLoading(true);
    try {
      const resp = await api.post(apiURl.login, payload, { auth: false });

      if (!isSuccessResponse(resp)) {
        swtAlert.error(
          "Sign In Failed",
          resp?.message || "Invalid email or password"
        );
        return;
      }

      const auth = extractAuthPayload(resp);
      dispatch(SetAuthUserData(auth));

      swtAlert.success(
        "Signed In",
        resp?.message || "Logged in successfully."
      );

      const from = location.state?.from?.pathname || DEFAULT_APP_PATH;
      const destination =
        auth.token && from !== "/" ? from : DEFAULT_APP_PATH;
      navigate(auth.token ? destination : "/login", { replace: true });
    } catch (err) {
      swtAlert.error(
        "Sign In Failed",
        err?.message || "Sign in failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <AuthSplitLayout>
      <form className="w-full max-w-sm space-y-6" onSubmit={handleSubmit} noValidate>
        <Logo showText text="Fortify" logoClassName="h-7 w-7" textClassName="text-base font-bold text-slate-900" />

        <div>
          <h1 className="text-3xl font-bold text-slate-900">Login to your account</h1>
          <p className="mt-2 text-sm text-slate-500">
            Use your backoffice credentials to continue.
          </p>
        </div>

        <div className="space-y-4">
          <FormTextInput
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleInputChange}
            inputClassName="bg-slate-100"
            error={errors.email}
          />
          <FormPasswordInput
            label="Password"
            name="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleInputChange}
            inputClassName="bg-slate-100"
            error={errors.password}
          />
        </div>

        <Button type="submit" className="flex w-full items-center justify-center gap-2 py-3" disabled={isLoading}>
          {isLoading ? (
            <>
              <FiLoader className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
              Signing in...
            </>
          ) : (
            "Log in"
          )}
        </Button>

        {/* <Link
          to="/forgot-password"
          className="block w-full text-center text-sm text-slate-600 underline decoration-slate-600"
        >
          Forgot password?
        </Link> */}
      </form>
    </AuthSplitLayout>
  );
}
